import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, withCtx as _withCtx } from "vue";
import { computed, watch, getCurrentInstance } from 'vue';
import { useRoute } from 'vue-router';
import { getHttpInstance } from '@/composables/useApi';
import { useAppState } from '@/composables/useAppState';
import MenuBar from './components/elements/MenuBar.vue';
import MaintenanceMessage from './components/elements/MaintenanceMessage.vue';
import { useSystemStatus } from '@/composables/useSystemStatus';
import pkg from '../package.json';
import { captureSafeAreas } from '@/utils/native';
const defaultBackgroundColor = 'var(--background-color-main)';
const voucherShopMessage = "The voucher shop is currently unavailable while we carry out some essential maintenance.\nYou can find your previously purchased vouchers by tapping the button below.\nWe'll be as quick as we can, and should be up and running again in a few hours.\nThanks for your patience.";
export default /*@__PURE__*/ _defineComponent({
    __name: 'App',
    setup(__props) {
        const version = pkg.version;
        const route = useRoute();
        const instance = getCurrentInstance();
        const cookies = instance?.appContext.config.globalProperties.$cookies;
        const state = useAppState();
        // The host puts the safe-area insets on the launch URL. Captured here rather than in
        // useAuth()/MenuBar so it happens wherever the app lands, sign in included. immediate
        // covers the initial URL; the watch covers the first navigation resolving after setup.
        watch(() => route.query, (query) => captureSafeAreas(query, cookies), { immediate: true });
        function retrySystemStatus() {
            useSystemStatus().fetchSystemStatus();
        }
        // On cold load/refresh the first router beforeEach runs before apiConfig.api is set
        // (so its fetch is skipped) and no further navigation follows. useAuth() sets the
        // reactive state.api once ready — fetch the moment it does so the page survives a refresh.
        watch(() => state.api, (api) => {
            if (api && route.path !== '/Login')
                useSystemStatus().fetchSystemStatus();
        }, { immediate: true });
        // Shop/buy voucher routes to block when the store is down. Deliberately excludes
        // /MyVouchers/* and /Redeem so already-bought vouchers stay accessible.
        const voucherShopPaths = [
            '/vouchercategories', '/vouchercart', '/shopvouchers', '/recommendedvouchers',
            '/featuredvouchers', '/boostedvouchers', '/favoritevouchers',
            '/topsavingsvouchers', '/biggestdiscountsvouchers',
        ];
        const voucherShopBlocked = computed(() => {
            if (!state.systemStatus || state.systemStatus.voucherStoreAvailable)
                return false;
            const p = route.path.toLowerCase();
            if (p.startsWith('/myvouchers'))
                return false;
            return p.startsWith('/vouchers') || voucherShopPaths.includes(p);
        });
        state.appVersion = version;
        function applyMobileDarkMode(enabled) {
            document.documentElement.classList.toggle('mobile-dark-mode', enabled);
            // Update theme-color so iOS Safari picks white status-bar icons on dark.
            const themeMeta = document.querySelector('meta[name="theme-color"]');
            if (themeMeta) {
                themeMeta.setAttribute('content', enabled ? '#0f0f1e' : '#413655');
            }
        }
        applyMobileDarkMode(state.isDarkMode);
        watch(() => state.isDarkMode, (val) => {
            localStorage.setItem('mobileDarkMode', String(val));
            applyMobileDarkMode(val);
        });
        function getDeviceType() {
            return navigator.userAgent;
        }
        function trackEvent(eventCategory, eventSubCategory, name, onSuccess = null) {
            const data = route.params;
            const deviceType = getDeviceType();
            const appVersion = version;
            const platformInfo = deviceType.match(/\((.*?)\)/)?.[1] || '';
            console.log('platformInfo:', platformInfo);
            console.log('appVersion:', appVersion);
            const requestBody = {
                eventCategory: eventCategory,
                eventSubCategory: eventSubCategory,
                name: name,
                appVersion: appVersion,
                platformInfo: platformInfo
            };
            // Only include data if the object has properties
            if (Object.keys(data).length > 0) {
                const idValue = Object.values(data)[0];
                requestBody.data = idValue;
                requestBody.name = name.replace('/' + idValue, '');
            }
            const http = getHttpInstance();
            const url = route.meta.api + "/Customers/00000000-0000-0000-0000-000000000000/Events";
            if (!http.defaults.headers.common["Authorization"]) {
                if (cookies?.get('token')) {
                    http.defaults.headers.common = { "Authorization": "bearer " + cookies.get('token') };
                }
            }
            if (http.defaults.headers.common["Authorization"]) {
                http.post(url, requestBody)
                    .then((response) => {
                    if (onSuccess) {
                        onSuccess(response.data);
                    }
                })
                    .catch((result) => {
                    console.log('error', result);
                });
            }
            else {
                console.log('No auth header so skipping logging');
            }
        }
        const backgroundColor = computed(() => {
            if (state.isDarkMode && route.meta.requiresAdmin) {
                return 'var(--background-dark-mode)';
            }
            if (route.meta.backgroundColor) {
                return route.meta.backgroundColor;
            }
            if (route.query.theme) {
                return route.query.theme === 'dark' ? '#332847' : 'white';
            }
            return defaultBackgroundColor;
        });
        const minHeight = computed(() => {
            return 'calc(100vh - ' + (route.meta.bottomMenu ? 60 : 0) + 'px)';
        });
        const padding = computed(() => {
            const c = cookies?.get('sb');
            let sb = 0;
            if (c) {
                sb = parseInt(c);
            }
            return (route.meta.bottomMenu ? (80 + sb) : 0) + 'px';
        });
        watch(() => route.path, (newPath) => {
            console.log('Route changed to ' + newPath);
            trackEvent('App', 'Navigation', newPath);
            document.body.classList.remove('chat-active');
            document.body.classList.remove('hide-nav-bar');
            if (state.isDarkMode && localStorage.getItem('showHiddenSettings') !== 'true') {
                state.isDarkMode = false;
            }
        });
        return (_ctx, _cache) => {
            const _component_router_view = _resolveComponent("router-view");
            const _component_v_app = _resolveComponent("v-app");
            return (_openBlock(), _createBlock(_component_v_app, null, {
                default: _withCtx(() => [
                    (_unref(state).systemStatus && !_unref(state).systemStatus.systemAvailable)
                        ? (_openBlock(), _createBlock(MaintenanceMessage, {
                            key: 0,
                            overlay: true,
                            onAction: retrySystemStatus
                        }))
                        : _createCommentVNode("", true),
                    _createElementVNode("div", {
                        ref: "body",
                        id: "body",
                        style: _normalizeStyle('background-color: ' + (backgroundColor.value + ';' + (_ctx.$route.meta.backgroundImage ? 'background: url(' + _ctx.$route.meta.backgroundImage + ')' : '')))
                    }, [
                        _createElementVNode("div", {
                            id: "app",
                            style: _normalizeStyle('min-height: ' + minHeight.value + '; padding-bottom:' + padding.value)
                        }, [
                            (voucherShopBlocked.value)
                                ? (_openBlock(), _createBlock(MaintenanceMessage, {
                                    key: 0,
                                    title: "Voucher shop is taking a short break",
                                    message: voucherShopMessage,
                                    buttonText: "My vouchers",
                                    secondaryText: "Back",
                                    onAction: _cache[0] || (_cache[0] = ($event) => (_ctx.$router.push('/Wallet'))),
                                    onSecondary: _cache[1] || (_cache[1] = ($event) => (_ctx.$router.back()))
                                }))
                                : (_openBlock(), _createBlock(_component_router_view, {
                                    key: JSON.stringify(_ctx.$route.query)
                                })),
                            (_ctx.$route.meta.bottomMenu && !_ctx.$route.query.inModal && !_ctx.$route.query.nomenu)
                                ? (_openBlock(), _createBlock(MenuBar, { key: 2 }))
                                : _createCommentVNode("", true)
                        ], 4)
                    ], 4)
                ]),
                _: 1
            }));
        };
    }
});
